'use client'

import { useEffect, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'react-i18next'
import { setStoredLanguage, getStoredLanguage } from '@/lib/i18n/utils'

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status, update } = useSession()
  const { i18n } = useTranslation()
  const hasInitializedRef = useRef(false)
  const [isHydrated, setIsHydrated] = useState(false)

  // 处理客户端水合后的语言同步
  useEffect(() => {
    setIsHydrated(true)
    
    // 立即从 localStorage 读取语言设置
    const storedLanguage = getStoredLanguage()
    if (storedLanguage && i18n.language !== storedLanguage) {
      i18n.changeLanguage(storedLanguage)
    }
  }, [i18n])

  useEffect(() => {
    // 在用户登录后同步语言设置
    if (isHydrated && status === 'authenticated' && session?.user && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      
      const storedLanguage = getStoredLanguage()
      const sessionLanguage = session.user.language
      
      // 优先级：localStorage > session > 当前语言
      // 如果有 localStorage，说明用户最近切换过语言
      if (storedLanguage) {
        // 如果当前语言与存储的不同，更新当前语言
        if (i18n.language !== storedLanguage) {
          i18n.changeLanguage(storedLanguage)
        }
      } else if (sessionLanguage) {
        // 没有 localStorage 但有 session 语言，使用 session 语言
        if (i18n.language !== sessionLanguage) {
          i18n.changeLanguage(sessionLanguage)
          setStoredLanguage(sessionLanguage)
        }
      } else {
        // 都没有，保存当前语言到 localStorage
        setStoredLanguage(i18n.language)
      }
    }
  }, [session, status, i18n, isHydrated])

  // 监听语言变化并保存
  useEffect(() => {
    const handleLanguageChanged = async (lng: string) => {
      setStoredLanguage(lng)
      
      // 异步更新数据库（不阻塞 UI）
      if (status === 'authenticated') {
        try {
          const response = await fetch('/api/user/settings', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ language: lng }),
          })
          
          if (response.ok && update) {
            // 更新成功后，刷新 session 以获取最新的语言设置
            // 这确保了 JWT token 包含最新的语言信息
            await update()
          }
        } catch (error) {
          console.error('Failed to update language in database:', error)
        }
      }
    }

    i18n.on('languageChanged', handleLanguageChanged)
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [i18n, status, update])

  return <>{children}</>
}